/**
 * DEV-276 (AC-17/18/19; D11) — catálogo LITERAL das mensagens de erro do envio
 * da planilha (anotação "possíveis cenários de erro" do frame
 * `Create / Mass / Error / File`, Figma 18356:83885) e o mapeamento causa →
 * mensagem. Toda mensagem aparece abaixo do campo "Planilha de vagas", com o
 * `Input` em erro; nenhuma leva a outra tela.
 */

export const JOB_IMPORT_MESSAGES = {
  // Arquivo
  size: 'O arquivo excede o limite de 20 MB.',
  format: 'Aceitamos só arquivos em XLSX. Salve nesse formato e envie de novo.',
  empty: 'Esse arquivo está vazio. Confira se ele salvou direito e envie de novo.',
  corrupted: 'Não foi possível ler esse XLSX. Exporte outra vez e envie o novo arquivo.',
  rejected: 'Não foi possível processar esse arquivo. Envie outra planilha.',
  // Conteúdo da planilha
  nothing:
    'Nenhuma vaga dessa planilha pode ser criada ou atualizada. Confira o preenchimento e envie de novo.',
  // Escolha do arquivo
  multiple: 'Dá para enviar um arquivo por vez. Escolha qual planilha você quer enviar.',
  folder: 'Arraste o arquivo, não a pasta. Abra a pasta e escolha o XLSX.',
  cloud: 'Esse arquivo ainda está na nuvem. Abra ele uma vez no computador e envie de novo.',
  permission: 'Precisamos de permissão para acessar seus arquivos. Autorize no seu navegador.',
  notFound: 'Não encontramos mais esse arquivo. Ele pode ter sido movido ou apagado.',
  locked: 'Esse arquivo está aberto em outro programa. Feche ele e envie de novo.',
  // Conexão
  offline: 'Sem conexão no momento. Assim que a internet voltar, é só enviar.',
  interrupted: 'O envio parou no caminho e nada foi salvo. Tente de novo com a conexão estável.',
  timeout: 'O envio demorou demais e nada foi salvo. Tente de novo em uma conexão mais estável.',
  captivePortal: 'Sua rede pediu um login. Faça o acesso do Wi-Fi e envie a planilha de novo.',
  // Servidor
  server: 'O problema foi do nosso lado. Tente de novo em alguns minutos.',
  maintenance: 'Estamos em manutenção. O envio de planilhas volta em instantes.',
  tooMany: 'Você fez várias tentativas seguidas. Espere alguns minutos e tente de novo.',
  storage: 'Sua conta atingiu o limite de armazenamento. Fale com o suporte para liberar espaço.',
  unconfirmed: 'Não conseguimos confirmar o envio. Recarregue a tela para conferir.',
  incomplete: 'O arquivo chegou incompleto. Envie de novo — costuma resolver na segunda tentativa.',
  stale: 'Esta tela ficou desatualizada. Recarregue a página para continuar de onde você parou.',
  // Durante o envio
  reloaded:
    'A página foi recarregada no meio do envio e nada foi salvo. Escolha o arquivo de novo.',
  background: 'O envio parou enquanto a aba ficou em segundo plano. Escolha o arquivo de novo.',
  // Fora do catálogo do Figma (o frame não desenha "sem arquivo": o botão fica
  // desabilitado). Guarda do envio sem planilha escolhida; mesmo tom das
  // mensagens "Selecione o programa" / "Selecione o tipo de vaga" do form.
  fileRequired: 'Selecione a planilha de vagas',
} as const;

export type JobImportMessageKey = keyof typeof JOB_IMPORT_MESSAGES;

export const JOB_IMPORT_MAX_BYTES = 20 * 1024 * 1024;
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Erro nomeado do BFF/Core (status + `error`). */
export class JobImportApiError extends Error {
  readonly name = 'JobImportApiError';
  constructor(
    readonly status: number,
    readonly code: string | null,
    readonly body: unknown = null
  ) {
    super(`Job import failed (status ${status}${code ? `, ${code}` : ''})`);
  }
}

/** Verificações locais, antes de qualquer request (AC-17): formato, tamanho, vazio. */
export function localFileError(file: File): string | null {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.xlsx') && file.type !== XLSX_MIME) return JOB_IMPORT_MESSAGES.format;
  if (file.size === 0) return JOB_IMPORT_MESSAGES.empty;
  if (file.size > JOB_IMPORT_MAX_BYTES) return JOB_IMPORT_MESSAGES.size;
  return null;
}

/** Mais de um arquivo escolhido/arrastado (AC-16). */
export function fileListError(files: FileList | File[] | null | undefined): string | null {
  if (!files) return null;
  if (files.length > 1) return JOB_IMPORT_MESSAGES.multiple;
  return null;
}

const CODE_TO_MESSAGE: Record<string, string> = {
  file_too_large: JOB_IMPORT_MESSAGES.size,
  file_not_xlsx: JOB_IMPORT_MESSAGES.format,
  file_missing: JOB_IMPORT_MESSAGES.format,
  file_empty: JOB_IMPORT_MESSAGES.empty,
  file_corrupted: JOB_IMPORT_MESSAGES.corrupted,
  sheet_invalid: JOB_IMPORT_MESSAGES.corrupted,
  sheet_row_limit: JOB_IMPORT_MESSAGES.rejected,
  file_rejected: JOB_IMPORT_MESSAGES.rejected,
  nothing_to_import: JOB_IMPORT_MESSAGES.nothing,
  core_unreachable: JOB_IMPORT_MESSAGES.server,
  core_bad_gateway: JOB_IMPORT_MESSAGES.server,
};

/** Causa (erro do request, do navegador ou do servidor) → mensagem do catálogo. */
export function uploadErrorMessage(err: unknown): string {
  if (err instanceof JobImportApiError) {
    const byCode = err.code ? CODE_TO_MESSAGE[err.code] : undefined;
    if (byCode) return byCode;
    if (err.status === 413) return JOB_IMPORT_MESSAGES.size;
    if (err.status === 415) return JOB_IMPORT_MESSAGES.format;
    if (err.status === 401 || err.status === 404) return JOB_IMPORT_MESSAGES.stale;
    if (err.status === 429) return JOB_IMPORT_MESSAGES.tooMany;
    if (err.status === 503) return JOB_IMPORT_MESSAGES.maintenance;
    if (err.status >= 500) return JOB_IMPORT_MESSAGES.server;
    return JOB_IMPORT_MESSAGES.rejected;
  }
  const e = err as { name?: string; message?: string } | null;
  const name = e?.name ?? '';
  if (name === 'TimeoutError') return JOB_IMPORT_MESSAGES.timeout;
  if (name === 'AbortError') return JOB_IMPORT_MESSAGES.interrupted;
  if (name === 'NotFoundError') return JOB_IMPORT_MESSAGES.notFound;
  if (name === 'NotReadableError') return JOB_IMPORT_MESSAGES.locked;
  if (name === 'SecurityError' || name === 'NotAllowedError') return JOB_IMPORT_MESSAGES.permission;
  if (typeof navigator !== 'undefined' && navigator.onLine === false)
    return JOB_IMPORT_MESSAGES.offline;
  if (name === 'TypeError') return JOB_IMPORT_MESSAGES.interrupted;
  return JOB_IMPORT_MESSAGES.server;
}

/**
 * DEV-509 AC-2/AC-3 — o erro do envio é do ARQUIVO ou do CAMINHO até o servidor? Sem
 * conexão, envio interrompido, tempo esgotado, servidor fora ou ocupado: o mesmo arquivo
 * serve para tentar de novo, então "Criar vagas" continua habilitado. Formato, tamanho,
 * vazio, corrompido, conteúdo da planilha, arquivo que não dá para ler no disco: é preciso
 * escolher outro arquivo.
 */
export function isRetryableUploadError(err: unknown): boolean {
  if (err instanceof JobImportApiError) {
    // Código com mensagem própria: só o do servidor fora (core_unreachable/bad_gateway) se repete.
    const byCode = err.code ? CODE_TO_MESSAGE[err.code] : undefined;
    if (byCode) return byCode === JOB_IMPORT_MESSAGES.server;
    return err.status >= 500 || err.status === 429;
  }
  const name = (err as { name?: string } | null)?.name ?? '';
  if (name === 'TimeoutError' || name === 'AbortError' || name === 'TypeError') return true;
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Falha ao confirmar (AC-42): fica na Confirmação com esta mensagem. */
export const CONFIRM_ERROR_MESSAGE = 'Algo deu errado. Tente novamente';
export const IMPORT_CHANGED_MESSAGE =
  'As vagas do programa mudaram desde o envio e nada foi gravado. Volte e envie a planilha de novo.';

export function confirmErrorMessage(err: unknown): string {
  if (err instanceof JobImportApiError && err.code === 'import_changed')
    return IMPORT_CHANGED_MESSAGE;
  if (err instanceof JobImportApiError && (err.status === 401 || err.status === 404))
    return JOB_IMPORT_MESSAGES.stale;
  return CONFIRM_ERROR_MESSAGE;
}
